import { lazy, type LazyExoticComponent, type ComponentType } from 'react';
import { logger } from '../lib/logger.js';
import type { RuntimeWindowClass } from './runtimeWindowClasses.js';
import { joinCoreRoster, webExtensions } from './platformExtensions.js';

/**
 * Static page component registry for manifest-driven plugin routing (Faz 6, #183).
 *
 * Keys correspond to `component` field in manifest.pages[].
 * Each value is a lazy() import — separate chunks, no manualChunks (memory: project_vite_manualchunks_tdz).
 */
const CORE_PAGE_COMPONENTS: Record<string, LazyExoticComponent<ComponentType<any>>> = {
  // Chat plugin (Sprint A5, roadmap #188)
  ChatPage: lazy(() => import('../modules/chat/ChatPage.js').then((m) => ({ default: m.ChatPage }))),

  // Notes plugin
  NotesPage: lazy(() => import('../pages/notes/NotesPage.js').then((m) => ({ default: m.NotesPage }))),

  // Email Admin plugin — all pages use default export
  EmailAdminLayout: lazy(() => import('../pages/emailAdmin/EmailAdminLayout.js')),
  EmailProfilesPage: lazy(() => import('../pages/emailAdmin/EmailProfilesPage.js')),
  EmailAddressesPage: lazy(() => import('../pages/emailAdmin/EmailAddressesPage.js')),
  EmailTemplatesPage: lazy(() => import('../pages/emailAdmin/EmailTemplatesPage.js')),
  EmailLogsPage: lazy(() => import('../pages/emailAdmin/EmailLogsPage.js')),
  EmailSettingsPage: lazy(() => import('../pages/emailAdmin/EmailSettingsPage.js')),

  // Security module (admin-only; api modules/security/module.manifest.ts) — default exports
  SecurityLayout: lazy(() => import('../pages/security/SecurityLayout.js')),
  SecurityAccessPage: lazy(() => import('../pages/security/SecurityAccessPage.js')),
  SecurityFindingsPage: lazy(() => import('../pages/security/SecurityFindingsPage.js')),
  SecurityPosturePage: lazy(() => import('../pages/security/SecurityPosturePage.js')),
  SecurityAgentsPage: lazy(() => import('../pages/security/SecurityAgentsPage.js')),

  // Chat Admin plugin — Faz 4
  ChatAdminLayout: lazy(() => import('../pages/chatAdmin/ChatAdminLayout.js')),
  ChatCategoriesPage: lazy(() => import('../pages/chatAdmin/ChatCategoriesPage.js')),
  ChatChannelsPage: lazy(() => import('../pages/chatAdmin/ChatChannelsPage.js')),

  // AI Hub plugin
  AIPage: lazy(() => import('../pages/ai/AIPage.js')),
  AISettingsPage: lazy(() => import('../pages/ai/AISettingsPage.js')),

  // Desktop Compositor plugin — Faz 2 (roadmap #217)
  desktop_compositor: lazy(() => import('../modules/desktop/CompositorPage.js')),

  // Composer Telemetry plugin — observability (ai.compose.mutate audit log)
  ComposerTelemetryPage: lazy(() =>
    import('../modules/composerTelemetry/ComposerTelemetryPage.js').then((m) => ({
      default: m.ComposerTelemetryPage,
    })),
  ),

  // Tmux plugin — session manager + xterm.js terminal (roadmap #229)
  TmuxPage: lazy(() => import('../modules/tmux/TmuxPage.js')),

  // AI Debate plugin — debate session UI (roadmap #380 Sprint B)
  AiDebatePage: lazy(() =>
    import('../modules/ai-debate/AiDebatePage.js').then((m) => ({ default: m.AiDebatePage })),
  ),

  // Deployment plugin — environment registry (roadmap #406)
  DeploymentEnvsPage: lazy(() =>
    import('../modules/deployment/DeploymentEnvsPage.js').then((m) => ({
      default: m.DeploymentEnvsPage,
    })),
  ),

  // PM (Project Management) core module — windowable via the core windowable
  // registry (gh#1195). PmPage ships a default export, so no `.then()` unwrap.
  PmPage: lazy(() => import('../modules/pm/PmPage.js')),

  // `DmSprintDashboard` and `DmQuestionInbox` are NOT here (gh#2607 PR-5). Both were
  // windowable dm pages and both retired into the `/dm` shell: sprint membership is the
  // board's tree section (with the corpus's freshness on the Overview tab), and the
  // decision inbox became the shell's `Inbox` TAB — the same component, re-homed, so the
  // `pm.question.answer` verb kept a surface. A key removed from here is refused by
  // `resolvePageComponent`, which is what a saved layout or a `?win=` naming one meets.

  // DM activity ledger (gh#2059 Faz 3 PR-9; renamed from `DmAgentRunPanel` at DM W2 A1,
  // when the flat run list became the sprint ▸ session ▸ run tree). Ships a default
  // export, so no `.then()` unwrap.
  //
  // THE OLD KEY IS GONE, AND THAT IS THE INTENDED FAILURE: a saved layout naming
  // `DmAgentRunPanel` meets `resolvePageComponent`'s refusal rather than a window that
  // renders something else under the old name. The window is re-openable from the
  // launcher, whose entry moved with the key.
  DmActivityTab: lazy(() => import('../modules/pm/DmActivityTab.js')),

  // DM project view host (gh#2059 K-G7-1 PR-2) — the pane-stack workspace frame.
  // Ships a default export, so no `.then()` unwrap.
  DmProjectView: lazy(() => import('../modules/pm/DmProjectView.js')),

  // DM memory search (SRS G7-C PR-3) — semantic recall over the workspace's memory
  // records. Ships a default export, so no `.then()` unwrap.
  DmMemorySearch: lazy(() => import('../modules/pm/DmMemorySearch.js')),

  // DM policies tab (W3-B UIa). Default export.
  DmPoliciesTab: lazy(() => import('../modules/pm/DmPoliciesTab.js')),

  // DM docs page (W3-B D3). Default export.
  DmDocsPage: lazy(() => import('../modules/pm/DmDocsPage.js')),

  // Entity OS Console core module (roadmap #474, epic #473). The desktop launcher resolves
  // the windowed console via this registry (PluginWindowAdapter → pageComponentRegistry);
  // without the entry it rendered "component not registered" (gh#1527). ConsolePage ships a
  // default export, so no `.then()` unwrap.
  ConsolePage: lazy(() => import('../modules/console/ConsolePage.js')),

  // Settings core module — windowable Settings surface (gh#1643, roadmap #490, PR-2). Hosts the
  // existing settings pages (Güvenlik / Bağlantılar) + the admin-gated platform-settings panel.
  // Registered here so the compositor launcher can open it as a window (3-point lockstep with
  // CORE_WINDOWABLE_PAGES + the /settings route). SettingsPage ships a default export.
  SettingsPage: lazy(() => import('../modules/settings/SettingsPage.js')),
};

/** Core pages plus the build-time web slot's components; a name core owns refuses the build. */
export const pageComponentRegistry: Record<string, LazyExoticComponent<ComponentType<any>>> = Object.fromEntries(
  joinCoreRoster(
    'page component',
    Object.entries(CORE_PAGE_COMPONENTS),
    // The slot types the loader without React; its default export is the page component.
    Object.entries(webExtensions.components).map(
      ([key, load]): [string, LazyExoticComponent<ComponentType<any>>] => [
        key,
        lazy(load as () => Promise<{ default: ComponentType<any> }>),
      ],
    ),
    ([key]) => [key],
  ),
);

/**
 * Snapshot of the STATIC (core + slot) page keys captured at module load — before any runtime
 * registerPageComponent call. These are permanent, shared across every window that opens
 * the same page; `unregisterPageComponent` refuses to drop them so a per-window cleanup
 * can never tear down a still-live core page (defense in depth — the store caller already
 * gates on `WindowState.ownsPageKey`). gh#1574.
 */
const CORE_PAGE_KEYS: ReadonlySet<string> = new Set(Object.keys(pageComponentRegistry));

/**
 * Resolve a page component by key — the ONE read seam for this registry.
 *
 * OWN-property only. A bare `pageComponentRegistry[key]` inherits every `Object.prototype`
 * member, so `key = 'toString'` (or `'constructor'`, `'valueOf'`, …) returns a live function
 * that is NOT a page component: the caller sees a truthy value, hands it to React as a lazy
 * element type and renders garbage instead of the honest "not registered" path. Keys reach
 * this function from untrusted places — a persisted layout snapshot, and from G7-W PR-3 a URL
 * query parameter — so the probe is fail-closed: anything that is not an own key is absent.
 *
 * Every reader goes through here (PluginWindowAdapter, desktopStore.applyLayout,
 * PluginRoutesHost's route table) so the invariant lives in one seam instead of being
 * re-derived at each call site. Add a new reader to this list when you add one — the seam is
 * only single as long as nobody indexes `pageComponentRegistry` directly.
 */
export function resolvePageComponent(
  key: string,
): LazyExoticComponent<ComponentType<any>> | undefined {
  if (!Object.hasOwn(pageComponentRegistry, key)) return undefined;
  return pageComponentRegistry[key];
}

/**
 * Resolve `key`, REHYDRATING it from a runtime window class when nothing is registered
 * under it (gh#2517).
 *
 * A runtime key (`UserViewWindow-5`) is registered only by the click that opened it, so it
 * is absent on every later load — and a layout snapshot naming it was therefore unrestorable
 * however faithfully it had been recorded. A class can rebuild the component from the key
 * alone, so this is the arm that turns "not registered" into "not registered YET".
 *
 * ── THIS READER WRITES, AND ONLY UNDER THREE CONDITIONS ──────────────────────
 * Every other function here is pure; this one mutates the module registry. The write is
 * bounded by:
 *  - an EXISTING registration always wins — a live key can never be replaced by a class, so
 *    no static or already-open page can be re-pointed at another component;
 *  - the class must ADMIT the key (`resolve` returns undefined for a malformed member), so
 *    an arbitrary string cannot cause a registration;
 *  - the caller must have AUTHORIZED the key first. `applyLayout` calls this only after its
 *    allowlist/class door has passed, so an unpermitted payload never reaches the write.
 *
 * The entry is left registered afterwards, deliberately: it is a stable key shared by every
 * window on that view, exactly like the key the click path registers, and `ownsPageKey` is
 * NOT set for anything a layout applies — so nothing unregisters it on close (gh#1574).
 */
export function resolveOrRehydratePageComponent(
  key: string,
  cls: RuntimeWindowClass,
): LazyExoticComponent<ComponentType<any>> | undefined {
  const existing = resolvePageComponent(key);
  if (existing) return existing;
  const built = cls.resolve(key);
  if (!built) return undefined;
  registerPageComponent(key, built);
  return built;
}

/**
 * Register a page component at runtime (Faz 7+ marketplace extension point).
 * Overwrites existing keys with a warning.
 */
export function registerPageComponent(
  key: string,
  comp: LazyExoticComponent<ComponentType<any>>,
): void {
  if (key in pageComponentRegistry) {
    logger.warn({ key }, '[pageRegistry] overriding existing key');
  }
  pageComponentRegistry[key] = comp;
}

/**
 * Unregister a dynamically-registered page component (gh#1574).
 *
 * Desktop launchers (openSpecWindow / openComposerWindow / openCompositionWindow) register a
 * UNIQUE, per-open lazy component keyed by a fresh UUID. Without teardown every window open
 * leaks one lazy-component reference into this module-level registry (bounded memory leak).
 * The owning window unregisters its key on close via `desktopStore.removeWindow`.
 *
 * Safety:
 * - Core (static) keys captured at module load are protected — passing one is a no-op with a
 *   warning, so a per-window cleanup can never drop a shared page still used by a sibling.
 * - Idempotent: unregistering an absent key is a silent no-op (`delete` on a missing key is a
 *   no-op), so a double-close or an already-cleaned key never throws.
 *
 * Only per-OPEN unique keys are safe to pass — never a key shared across windows.
 */
export function unregisterPageComponent(key: string): void {
  if (CORE_PAGE_KEYS.has(key)) {
    logger.warn({ key }, '[pageRegistry] refusing to unregister core page key');
    return;
  }
  delete pageComponentRegistry[key];
}
